import axios from "axios";
import { del, idb, meta, put, putMany, row, rows, setMeta } from "./db";
import { pathOf } from "./mirror";
import { getActiveWorkspaceId } from "@/lib/workspace";
import { getSessionToken } from "@/lib/authToken";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Plain client used while replaying the queue — no offline interception. */
const net = axios.create({ baseURL: API, withCredentials: true, timeout: 60000 });
net.interceptors.request.use((config) => {
  const ws = getActiveWorkspaceId();
  if (ws) config.params = { ...(config.params || {}), workspace_id: ws };
  const token = getSessionToken();
  if (token) {
    if (!config.headers) config.headers = {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const WRITE = ["post", "put", "patch", "delete"];

/** Only diary work is allowed to queue — anything needing the server stays online-only. */
const QUEUEABLE = [
  /^\/events(\/|$)/,
  /^\/todos(\/|$)/,
  /^\/postits(\/|$)/,
  /^\/logs(\/|$)/,
  /^\/attachments\/[^/]+$/,
  /^\/notifications\//,
];

const ENTITY = {
  events: ["events", "event_id"],
  todos: ["todos", "todo_id"],
  postits: ["postits", "post_it_id"],
  logs: ["logs", "log_id"],
};

const nowIso = () => new Date().toISOString();
const localId = (kind) =>
  `local_${kind}_${Math.random().toString(36).slice(2, 8)}${Date.now().toString(36).slice(-4)}`;
export const isLocalId = (value) => typeof value === "string" && value.startsWith("local_");

/** Needs the server (LLM, email, sharing) — never queued while offline. */
const NEVER_QUEUE = [/^\/logs\/[^/]+\/brief$/];

export const canQueue = (config) => {
  const method = (config?.method || "get").toLowerCase();
  if (!WRITE.includes(method)) return false;
  const path = pathOf(config);
  if (NEVER_QUEUE.some((rule) => rule.test(path))) return false;
  return QUEUEABLE.some((rule) => rule.test(path));
};

const bodyOf = (config) => {
  const data = config?.data;
  if (!data) return null;
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return data;
};

const serialise = async (config) => {
  const data = config?.data;
  if (typeof FormData !== "undefined" && data instanceof FormData) {
    const parts = [];
    for (const [key, value] of data.entries()) {
      if (value instanceof Blob) parts.push({ key, blob: value, name: value.name || "upload" });
      else parts.push({ key, text: String(value) });
    }
    return { kind: "form", parts };
  }
  return { kind: "json", body: bodyOf(config) };
};

const rebuild = (payload) => {
  if (payload?.kind !== "form") return payload?.body ?? undefined;
  const form = new FormData();
  payload.parts.forEach((part) => {
    if (part.blob) form.append(part.key, part.blob, part.name);
    else form.append(part.key, part.text);
  });
  return form;
};

const cleanParams = (params = {}) => {
  const out = { ...params };
  delete out.workspace_id;
  return out;
};

const matterBits = async (matterId) => {
  if (!matterId) return {};
  const matter = await row("matters", matterId);
  return matter
    ? { matter_title: matter.title, case_number: matter.case_number, client_name: matter.client_name }
    : {};
};

const courtName = async (courtId) => {
  if (!courtId) return null;
  return (await row("courts", courtId))?.name || null;
};

/** What the diary held before this edit — used to spot a clash when we come back online. */
const snapshot = async (path, method) => {
  if (method === "post") return null;
  const parts = path.split("/");
  const entity = ENTITY[parts[1]];
  if (!entity || !parts[2] || isLocalId(parts[2])) return null;
  const current = await row(entity[0], parts[2]);
  if (!current) return null;
  return {
    check: `/${parts[1]}/${parts[2]}`,
    store: entity[0],
    key: entity[1],
    id: parts[2],
    updated_at: current.updated_at || null,
    title: current.title || current.summary || "this entry",
    mine: current,
  };
};

const appendAttachment = async (logId, payload) => {
  const file = payload.parts?.find((p) => p.blob);
  const item = {
    attachment_id: localId("att"),
    log_id: logId,
    original_filename: file?.name || "Attachment",
    content_type: file?.blob?.type || "application/octet-stream",
    size: file?.blob?.size || 0,
    pending_sync: true,
    created_at: nowIso(),
  };
  const key = `/logs/${logId}/attachments`;
  const cached = (await row("kv", key))?.data || [];
  await put("kv", { data: [...cached, item], at: nowIso() }, key);
  const log = await row("logs", logId);
  if (log) await putMany("logs", [{ ...log, attachments: [...(log.attachments || []), item] }]);
  return item;
};

/** Writes the change into the mirror so the screen behaves as if it saved. */
const applyLocal = async (path, method, body, payload) => {
  const parts = path.split("/");
  const kind = parts[1];
  const id = parts[2];
  const tail = parts[3];

  if (kind === "events") {
    if (method === "post") {
      const doc = {
        ...body,
        event_id: localId("event"),
        court_name: await courtName(body?.court_id),
        ...(await matterBits(body?.matter_id)),
        is_placeholder: Boolean(body?.is_placeholder) || !body?.matter_id,
        pending_sync: true,
        created_at: nowIso(),
        updated_at: nowIso(),
      };
      await putMany("events", [doc]);
      return { data: doc, temp: doc.event_id };
    }
    const prev = (await row("events", id)) || { event_id: id };
    if (method === "delete") {
      await del("events", id);
      return { data: { ok: true, event_id: id } };
    }
    let patch = {};
    if (tail === "move") patch = { date: body?.date };
    else if (tail === "matter")
      patch = {
        matter_id: body?.matter_id || null,
        is_placeholder: !body?.matter_id,
        ...(await matterBits(body?.matter_id)),
      };
    else
      patch = {
        ...body,
        court_name: await courtName(body?.court_id ?? prev.court_id),
        ...(await matterBits(body?.matter_id ?? prev.matter_id)),
      };
    const doc = { ...prev, ...patch, pending_sync: true, updated_at: nowIso() };
    await putMany("events", [doc]);
    return { data: tail === "move" ? { ok: true, event: doc } : doc };
  }

  if (kind === "todos") {
    if (method === "post" && !id) {
      const doc = {
        ...body,
        todo_id: localId("todo"),
        done: false,
        ...(await matterBits(body?.matter_id)),
        pending_sync: true,
        created_at: nowIso(),
        updated_at: nowIso(),
      };
      await putMany("todos", [doc]);
      return { data: doc, temp: doc.todo_id };
    }
    const prev = (await row("todos", id)) || { todo_id: id };
    if (method === "delete") {
      await del("todos", id);
      return { data: { ok: true, todo_id: id } };
    }
    const patch = tail === "toggle" ? { done: !prev.done } : { ...body, ...(await matterBits(body?.matter_id ?? prev.matter_id)) };
    const doc = { ...prev, ...patch, pending_sync: true, updated_at: nowIso() };
    await putMany("todos", [doc]);
    return { data: doc };
  }

  if (kind === "postits") {
    if (method === "post" && (!id || id === "voice")) {
      const voice = id === "voice";
      const doc = {
        ...(voice ? {} : body),
        post_it_id: localId("note"),
        title: voice ? "Voice note — waiting to transcribe" : body?.title || null,
        body: voice
          ? "Recorded offline. It will be written up as soon as this iPad is back online."
          : body?.body || "",
        source: voice ? "voice" : "typed",
        priority: body?.priority || "normal",
        pinned: false,
        expired: false,
        has_audio: false,
        has_image: false,
        pending_sync: true,
        pending_voice: voice,
        created_at: nowIso(),
        updated_at: nowIso(),
        ...(await matterBits(body?.matter_id)),
      };
      await putMany("postits", [doc]);
      const order = (await meta("postit_order")) || [];
      await setMeta("postit_order", [doc.post_it_id, ...order]);
      return { data: doc, temp: doc.post_it_id };
    }
    if (id === "reorder") {
      await setMeta("postit_order", body?.ids || []);
      return { data: { ok: true } };
    }
    const prev = (await row("postits", id)) || { post_it_id: id };
    if (method === "delete" && !tail) {
      await del("postits", id);
      return { data: { ok: true, post_it_id: id } };
    }
    if (tail === "todo" || tail === "event") {
      const title = prev.title || (prev.body || "").split("\n")[0]?.slice(0, 120) || "Note";
      if (tail === "todo") {
        const todo = {
          todo_id: localId("todo"),
          date: body?.date,
          title,
          notes: prev.body || null,
          matter_id: prev.matter_id || null,
          priority: prev.priority === "high" ? "high" : "normal",
          done: false,
          pending_sync: true,
          created_at: nowIso(),
          updated_at: nowIso(),
          ...(await matterBits(prev.matter_id)),
        };
        await putMany("todos", [todo]);
        await putMany("postits", [{ ...prev, todo_id: todo.todo_id, todo_title: title }]);
        return { data: { ok: true, todo }, temp: todo.todo_id };
      }
      const event = {
        event_id: localId("event"),
        date: body?.date,
        title,
        notes: prev.body || null,
        matter_id: prev.matter_id || null,
        event_type: "post_court",
        urgent: prev.priority === "high",
        is_placeholder: !prev.matter_id,
        pending_sync: true,
        created_at: nowIso(),
        updated_at: nowIso(),
        ...(await matterBits(prev.matter_id)),
      };
      await putMany("events", [event]);
      await putMany("postits", [{ ...prev, event_id: event.event_id, event_label: title }]);
      return { data: { ok: true, event }, temp: event.event_id };
    }
    const doc = { ...prev, ...body, pending_sync: true, updated_at: nowIso() };
    await putMany("postits", [doc]);
    return { data: doc };
  }

  if (kind === "logs") {
    if (tail === "attachments") return { data: await appendAttachment(id, payload) };
    if (tail === "link") {
      const prev = (await row("logs", id)) || { log_id: id };
      const doc = {
        ...prev,
        matter_id: body?.matter_id || null,
        needs_matter: !body?.matter_id,
        ...(await matterBits(body?.matter_id)),
        updated_at: nowIso(),
      };
      await putMany("logs", [doc]);
      return { data: doc };
    }
    if (id === "voice" || id === "dictate") {
      const fields = Object.fromEntries(
        (payload.parts || []).filter((p) => p.text !== undefined).map((p) => [p.key, p.text]),
      );
      const doc = {
        log_id: localId("log"),
        matter_id: fields.matter_id || null,
        event_id: fields.event_id || null,
        date: fields.date || nowIso().slice(0, 10),
        kind: "note",
        body_html:
          "<p><em>Dictated in court. It will be written up here as soon as this device is back online.</em></p>",
        author_name: (await row("kv", "/auth/me"))?.data?.name || "You",
        source: "voice",
        attachments: [],
        review_pending: true,
        pending_sync: true,
        pending_voice: true,
        created_at: nowIso(),
        updated_at: nowIso(),
        ...(await matterBits(fields.matter_id)),
      };
      await putMany("logs", [doc]);
      return { data: doc, temp: doc.log_id };
    }
    if (tail === "reviewed") {
      const prev = (await row("logs", id)) || { log_id: id };
      const doc = {
        ...prev,
        review_pending: false,
        ...(body?.drop_next_date ? { next_date: null, next_date_from_voice: false } : {}),
        updated_at: nowIso(),
      };
      await putMany("logs", [doc]);
      return { data: doc };
    }
    if (method === "post" && !id) {
      const user = (await row("kv", "/auth/me"))?.data;
      const doc = {
        ...body,
        log_id: localId("log"),
        author_name: user?.name || "You",
        attachments: [],
        pending_sync: true,
        created_at: nowIso(),
        updated_at: nowIso(),
      };
      await putMany("logs", [doc]);
      return { data: doc, temp: doc.log_id };
    }
    const prev = (await row("logs", id)) || { log_id: id };
    if (method === "delete") {
      await del("logs", id);
      return { data: { ok: true, log_id: id } };
    }
    const doc = { ...prev, ...body, pending_sync: true, updated_at: nowIso() };
    await putMany("logs", [doc]);
    return { data: doc };
  }

  return { data: { ok: true, queued: true } };
};

const labelFor = (path, method, body) => {
  const kind = path.split("/")[1];
  const verb = method === "post" ? "New" : method === "delete" ? "Removed" : "Edited";
  const noun =
    kind === "events"
      ? "diary entry"
      : kind === "todos"
        ? "to-do"
        : kind === "postits"
          ? "post-it"
          : kind === "logs"
            ? "hearing log"
            : "change";
  const title = body?.title || body?.summary || "";
  return `${verb} ${noun}${title ? ` — ${String(title).slice(0, 60)}` : ""}`;
};

/** Stores the change locally and parks the request until the connection is back. */
export const enqueue = async (config) => {
  const path = pathOf(config);
  const method = (config.method || "post").toLowerCase();
  const payload = await serialise(config);
  const body = payload.kind === "json" ? payload.body : null;
  const base = await snapshot(path, method);
  const applied = await applyLocal(path, method, body, payload);
  const db = await idb();
  await db.add("queue", {
    method,
    path,
    params: cleanParams(config.params),
    payload,
    base,
    temp: applied.temp || null,
    label: labelFor(path, method, body),
    at: nowIso(),
  });
  return applied.data;
};

export const pendingItems = () => rows("queue");
export const pendingCount = async () => (await rows("queue")).length;
export const conflictList = () => rows("conflicts");

const remap = (value, map) => {
  if (typeof value === "string") return map[value] || value;
  if (Array.isArray(value)) return value.map((v) => remap(v, map));
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, remap(v, map)]));
  }
  return value;
};

/** Puts whatever the server gave back into the mirror and remembers the new id. */
const absorb = async (item, data, map) => {
  if (!data || typeof data !== "object") return;
  const entity = ENTITY[item.path.split("/")[1]];
  const pairs = [
    [entity, data],
    [ENTITY.todos, data.todo],
    [ENTITY.events, data.event],
    [ENTITY.postits, data.post_it],
  ];
  for (const [target, doc] of pairs) {
    if (!target || !doc || typeof doc !== "object") continue;
    const [store, key] = target;
    if (!doc[key]) continue;
    if (item.temp && isLocalId(item.temp)) {
      await del(store, item.temp);
      map[item.temp] = doc[key];
    }
    await putMany(store, [doc]);
  }
};

let flushing = false;

/** Replays queued changes oldest first. Stops at the first network failure. */
export const flush = async () => {
  if (flushing) return null;
  flushing = true;
  const result = { applied: 0, failed: [], conflicts: 0, stopped: false };
  try {
    const db = await idb();
    const map = { ...((await meta("idmap")) || {}) };
    for (;;) {
      const [item] = await db.getAll("queue", null, 1);
      if (!item) break;
      const path = remap(item.path, map);
      const payload =
        item.payload?.kind === "json"
          ? { ...item.payload, body: remap(item.payload.body, map) }
          : item.payload;

      if (item.base?.check && !isLocalId(item.base.id)) {
        try {
          const { data: server } = await net.get(remap(item.base.check, map));
          if (server && item.base.updated_at && server.updated_at !== item.base.updated_at) {
            await put("conflicts", {
              id: `cf_${item.seq}`,
              label: item.label,
              at: item.at,
              store: item.base.store,
              key: item.base.key,
              server,
              mine: item.base.mine,
              item: { ...item, path, payload },
            });
            await db.delete("queue", item.seq);
            result.conflicts += 1;
            continue;
          }
        } catch (e) {
          if (!e.response) {
            result.stopped = true;
            break;
          }
          if (e.response.status === 404) {
            result.failed.push(`${item.label} — it was removed from the diary`);
            await db.delete("queue", item.seq);
            continue;
          }
        }
      }

      try {
        const res = await net.request({
          url: path,
          method: item.method,
          params: item.params,
          data: rebuild(payload),
        });
        await absorb({ ...item, path }, res.data, map);
        result.applied += 1;
        await db.delete("queue", item.seq);
      } catch (e) {
        if (!e.response) {
          result.stopped = true;
          break;
        }
        result.failed.push(`${item.label} — ${e.response?.data?.detail || "the diary refused it"}`);
        await db.delete("queue", item.seq);
      }
    }
    await setMeta("idmap", map);
  } finally {
    flushing = false;
  }
  return result;
};

/** "mine" re-sends the offline edit, "theirs" keeps what the diary already holds. */
export const settleConflict = async (conflictId, choice) => {
  const conflict = await row("conflicts", conflictId);
  if (!conflict) return;
  if (choice === "mine") {
    const db = await idb();
    const { seq, ...item } = conflict.item || {};
    await db.add("queue", { ...item, base: null });
  } else if (conflict.server) {
    await putMany(conflict.store, [conflict.server]);
  }
  await del("conflicts", conflictId);
};

export const discardQueue = async () => {
  const db = await idb();
  await db.clear("queue");
  await db.clear("conflicts");
};
